// Service API pour l'historique du chat (remplace chatHistoryService.ts avec localStorage)
import api from '../config/api';
import { ChatInteraction } from './chatHistoryService';

export interface ChatInteractionApi {
  id: number;
  question: string;
  answer: string;
  timestamp: string;
  source: 'api' | 'knowledge_base' | 'ai' | 'fallback';
  api_type?: 'prayer_times' | 'quran_verse' | 'hadith' | 'dua' | 'asma_ul_husna' | 'random_ayah';
  ai_model?: 'gemini' | 'groq';
  user_city?: string;
  user_latitude?: number;
  user_longitude?: number;
  response_time?: number;
  user_email?: string;
}

class ChatHistoryApiService {
  // Enregistrer une interaction
  async saveInteraction(interaction: Omit<ChatInteraction, 'id' | 'timestamp'>): Promise<void> {
    try {
      const payload = {
        question: interaction.question,
        answer: interaction.answer,
        source: interaction.source,
        api_type: interaction.apiType,
        ai_model: interaction.aiModel,
        user_city: interaction.userLocation?.city,
        user_latitude: interaction.userLocation?.lat,
        user_longitude: interaction.userLocation?.lng,
        response_time: interaction.responseTime,
      };

      await api.post('/chat/interactions/', payload);
      console.log(`[ChatHistory] Interaction sauvegardée: ${interaction.question.substring(0, 50)}...`);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de l\'interaction:', error);
      // Fallback vers localStorage si l'API échoue
      this.saveToLocalStorage(interaction);
    }
  }

  // Obtenir toutes les interactions (pour les admins)
  async getAllInteractions(filters?: {
    search?: string;
    source?: ChatInteraction['source'];
    dateFrom?: Date;
    dateTo?: Date;
    limit?: number;
    page?: number;
  }): Promise<{ results: ChatInteraction[]; count: number; next?: string; previous?: string }> {
    try {
      const params: any = {
        page: filters?.page || 1,
      };

      if (filters?.search) {
        params.search = filters.search;
      }
      if (filters?.source) {
        params.source = filters.source;
      }
      if (filters?.dateFrom) {
        params.date_from = filters.dateFrom.toISOString();
      }
      if (filters?.dateTo) {
        params.date_to = filters.dateTo.toISOString();
      }

      const response = await api.get('/chat/interactions/', { params });
      const data = response.data;

      return {
        results: (data.results || []).map(this.mapToChatInteraction),
        count: data.count || 0,
        next: data.next,
        previous: data.previous,
      };
    } catch (error) {
      console.error('Erreur lors du chargement des interactions:', error);
      // Fallback vers localStorage
      const interactions = this.getAllFromLocalStorage();
      return {
        results: this.filterInteractions(interactions, filters),
        count: interactions.length,
      };
    }
  }

  // Obtenir les statistiques (pour les admins)
  async getStatistics(): Promise<{
    total_interactions: number;
    by_source: Record<string, number>;
    by_api_type: Record<string, number>;
    by_ai_model: Record<string, number>;
    average_response_time: number;
    total_users: number;
    frequent_questions: Array<{ question: string; count: number }>;
  }> {
    try {
      const response = await api.get('/chat/interactions/stats/');
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      return {
        total_interactions: 0,
        by_source: {},
        by_api_type: {},
        by_ai_model: {},
        average_response_time: 0,
        total_users: 0,
        frequent_questions: [],
      };
    }
  }

  // Mapper l'interaction API vers ChatInteraction
  private mapToChatInteraction(interaction: ChatInteractionApi): ChatInteraction {
    return {
      id: interaction.id.toString(),
      question: interaction.question,
      answer: interaction.answer,
      timestamp: new Date(interaction.timestamp),
      source: interaction.source,
      apiType: interaction.api_type,
      aiModel: interaction.ai_model,
      userLocation: interaction.user_city
        ? {
            city: interaction.user_city,
            lat: interaction.user_latitude || 0,
            lng: interaction.user_longitude || 0,
          }
        : undefined,
      responseTime: interaction.response_time,
    };
  }

  // Filtrer les interactions
  private filterInteractions(
    interactions: ChatInteraction[],
    filters?: {
      search?: string;
      source?: ChatInteraction['source'];
      dateFrom?: Date;
      dateTo?: Date;
      limit?: number;
    }
  ): ChatInteraction[] {
    let filtered = [...interactions];

    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (i) =>
          i.question.toLowerCase().includes(searchLower) ||
          i.answer.toLowerCase().includes(searchLower)
      );
    }

    if (filters?.source) {
      filtered = filtered.filter((i) => i.source === filters.source);
    }

    if (filters?.dateFrom) {
      filtered = filtered.filter((i) => i.timestamp >= filters.dateFrom!);
    }

    if (filters?.dateTo) {
      filtered = filtered.filter((i) => i.timestamp <= filters.dateTo!);
    }

    if (filters?.limit) {
      filtered = filtered.slice(0, filters.limit);
    }

    return filtered;
  }

  // Fallback vers localStorage
  private saveToLocalStorage(interaction: Omit<ChatInteraction, 'id' | 'timestamp'>): void {
    try {
      const saved = localStorage.getItem('chat_interactions_history');
      const interactions = saved ? JSON.parse(saved) : [];
      const newInteraction: ChatInteraction = {
        ...interaction,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        timestamp: new Date(),
      };
      interactions.push(newInteraction);
      const limited = interactions.slice(-1000);
      localStorage.setItem(
        'chat_interactions_history',
        JSON.stringify(
          limited.map((i: ChatInteraction) => ({
            ...i,
            timestamp: i.timestamp.toISOString(),
          }))
        )
      );
    } catch (error) {
      console.error('Erreur lors de la sauvegarde dans localStorage:', error);
    }
  }

  private getAllFromLocalStorage(): ChatInteraction[] {
    try {
      const saved = localStorage.getItem('chat_interactions_history');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((i: any) => ({
          ...i,
          timestamp: new Date(i.timestamp),
        }));
      }
    } catch (error) {
      console.error('Erreur lors du chargement depuis localStorage:', error);
    }
    return [];
  }
}

export const chatHistoryApiService = new ChatHistoryApiService();

